import { Mic, FileText, BookOpen, List } from "lucide-react";
import toast from "react-hot-toast";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import {
  checkUsageLimit,
  incrementUsage,
} from "../../services/operations/chatbotAPI";
import { useTranslation } from "react-i18next";
const BASE_URL = process.env.REACT_APP_VOICE_TO_VOICE_URL;

export default function ChatInput({
  input,
  setInput,
  handleSend,
  chats,
  addMessage,
  setChats,
  activeChatIndex,
  isLoading,
  selectedSubject,
  selectedChapter,
  setSelectedSubject,
  setSelectedChapter,
  subjects,
  chapters,
}) {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const recognitionRef = useRef(null);
  const user = useSelector((state) => state.profile.user);
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("en-US");

  const chatsRef = useRef([]);
  const [pdfFile, setPdfFile] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [historyIndex, setHistoryIndex] = useState(-1);

  const getUserMessages = () => {
    if (!chats[activeChatIndex]) return [];
    return chats[activeChatIndex].messages.filter((m) => m.role === "user");
  };

  useEffect(() => {
    chatsRef.current = chats;
  }, [chats]);

  const handlePDFUpload = async (file) => {
    const allowed = await checkUsageLimit();
    if (!allowed) return;

    if (!file || file.type !== "application/pdf") {
      toast.error(t("chatInput.onlyPdfAllowed"));
      return;
    }

    setPdfFile(file);
    toast.success(t("chatInput.pdfUploaded"));
  };

  const handleQueryWithPDF = async () => {
    if (!input.trim()) {
      toast.error(t("chatInput.enterQuery"));
      return;
    }

    if (pdfFile) {
      toast.loading(t("chatInput.generatingPdf"));
      const conversationId =
        chats[activeChatIndex]?._id || chats[activeChatIndex]?.conversationId;

      try {
        const formData = new FormData();
        formData.append("file", pdfFile);
        formData.append("query", input);
        formData.append("user_email", user.email);
        formData.append("conversation_id", conversationId);
        formData.append("student_class", user.class);
        formData.append("user_id", user._id);

        const res = await axios.post(`${BASE_URL}/pdf`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        toast.dismiss();
        const result = res.data;
        const responseMsg = `📄 [${t("chatInput.downloadPdf")}](${result.pdf_download_link})`;

        const addUserMessage = addMessage(conversationId, "user", input)();
        const addBotMessage = addMessage(
          conversationId,
          "assistant",
          responseMsg,
        )();
        await Promise.all([addUserMessage, addBotMessage]);

        const updatedChats = [...chats];
        updatedChats[activeChatIndex].messages.push(
          { role: "user", content: input },
          { role: "assistant", content: responseMsg },
        );

        await incrementUsage();
        setChats(updatedChats);
        setInput("");
        setPdfFile(null);

        toast.success(t("chatInput.pdfGenerated"));
      } catch (err) {
        toast.dismiss();
        toast.error(t("chatInput.pdfQueryFailed"));
        console.error(err);
      } finally {
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
      return;
    }

    handleSend();
  };

  const startRecordingWithLanguage = (language) => {
    if (!("webkitSpeechRecognition" in window)) {
      toast.error(t("chatInput.speechNotSupported"));
      return;
    }

    const recognition = new window.webkitSpeechRecognition();
    recognition.lang = language;
    recognition.continuous = true;
    recognition.interimResults = true;

    recognition.onresult = (event) => {
      let text = "";
      for (let i = 0; i < event.results.length; i++) {
        text += event.results[i][0].transcript + " ";
      }
      setInput(text.trim());
    };

    recognition.onerror = (err) => {
      console.error("Speech recognition error:", err);
      toast.error(t("chatInput.micError"));
    };

    recognition.onend = () => {
      if (isRecording) recognition.start();
    };

    recognitionRef.current = recognition;
    recognition.start();
    setIsRecording(true);
    toast.success(t("chatInput.recordingStarted"));
  };

  const stopRecording = () => {
    if (recognitionRef.current) {
      recognitionRef.current.onend = null;
      recognitionRef.current.stop();
    }
    setIsRecording(false);
    toast.success(t("chatInput.recordingStopped"));
  };

  const handleKeyDown = (e) => {
    const userMessages = getUserMessages();

    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (userMessages.length > 0) {
        const newIndex =
          historyIndex === -1
            ? userMessages.length - 1
            : Math.max(0, historyIndex - 1);
        setHistoryIndex(newIndex);
        setInput(userMessages[newIndex].content);
      }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (historyIndex !== -1) {
        const newIndex =
          historyIndex === userMessages.length - 1 ? -1 : historyIndex + 1;
        setHistoryIndex(newIndex);
        setInput(newIndex === -1 ? "" : userMessages[newIndex].content);
      }
    } else if (e.key === "Enter") {
      handleQueryWithPDF();
    }
  };

  return (
    <footer className="p-4 border-t border-gray-700">
      <div className="flex flex-col gap-2">
        {/* Subject and Chapter Dropdowns */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div className="flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-gray-400" />
            <select
              value={selectedSubject}
              onChange={(e) => {
                setSelectedSubject(e.target.value);
                setSelectedChapter("");
              }}
              className="flex-1 p-2 rounded-md bg-gray-800 text-white border border-gray-600 text-sm focus:border-blue-500 outline-none"
              disabled={subjects.length === 0}
            >
              <option value="">
                {subjects.length === 0
                  ? t("chatInput.loadingSubjects")
                  : t("chatInput.selectSubject")}
              </option>
              {subjects.map((subject) => (
                <option key={subject.id} value={subject.id}>
                  {subject.name}
                </option>
              ))}
            </select>
          </div>

          <div className="relative flex items-center gap-2">
            <List className="w-4 h-4 text-gray-400" />
            <select
              value={selectedChapter}
              onChange={(e) => setSelectedChapter(e.target.value)}
              disabled={!selectedSubject || chapters.length === 0}
              className="flex-1 p-2 rounded-md bg-gray-800 text-white border border-gray-600 text-sm focus:border-blue-500 outline-none disabled:opacity-50 truncate"
              title={selectedChapter}
            >
              <option value="">
                {!selectedSubject
                  ? t("chatInput.selectSubjectFirst")
                  : chapters.length === 0
                    ? t("chatInput.noChapters")
                    : t("chatInput.selectChapter")}
              </option>
              {chapters.map((chapter, idx) => (
                <option key={idx} value={chapter}>
                  {chapter.length > 50 ? chapter.slice(0, 50) + "..." : chapter}
                </option>
              ))}
            </select>
          </div>
        </div>

        {isLoading && (
          <div className="flex items-center space-x-2">
            <div className="flex space-x-1">
              <span className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-bounce"></span>
              <span
                className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-bounce"
                style={{ animationDelay: "0.15s" }}
              ></span>
              <span
                className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-bounce"
                style={{ animationDelay: "0.3s" }}
              ></span>
            </div>
            <span className="text-sm text-gray-400 font-medium">
              {t("chatInput.aiThinking")}
            </span>
          </div>
        )}

        <div className="flex items-center gap-2">
          <input
            className="flex-1 p-2 rounded-md bg-gray-800 text-white border border-gray-600"
            type="text"
            placeholder={t("chatInput.typeQuery")}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
          />

          {user?.role === "student" && (
            <button
              onClick={() => fileInputRef.current.click()}
              className="bg-gray-700 hover:bg-gray-600 p-2 rounded-md"
              title={t("chatInput.uploadPdf")}
            >
              <FileText className="w-5 h-5 text-white" />
            </button>
          )}

          <input
            type="file"
            accept="application/pdf"
            ref={fileInputRef}
            className="hidden"
            onChange={(e) => handlePDFUpload(e.target.files[0])}
          />

          <button
            className={`p-2 rounded-md transition ${
              isRecording
                ? "bg-red-500 hover:bg-red-600"
                : "bg-gray-700 hover:bg-gray-600"
            }`}
            title={
              isRecording
                ? t("chatInput.stopRecording")
                : t("chatInput.startRecording")
            }
            onClick={() => {
              if (isRecording) stopRecording();
              else setShowLanguageModal(true);
            }}
          >
            <Mic className="w-5 h-5 text-white" />
          </button>

          <button
            className="bg-green-600 hover:bg-green-700 px-4 py-2 rounded-md"
            onClick={handleQueryWithPDF}
          >
            {t("chatInput.send")}
          </button>
        </div>

        {pdfFile && (
          <div className="text-sm text-gray-400">
            ✅ {t("chatInput.pdfUploadedLabel")}{" "}
            <span className="font-medium">{pdfFile.name}</span>
          </div>
        )}
      </div>

      {showLanguageModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/40 backdrop-blur-md transition-opacity">
          <div className="w-full max-w-[360px] bg-white rounded-[24px] shadow-2xl shadow-indigo-900/10 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            {/* Header Section */}
            <div className="p-6 pb-4 border-b border-gray-100 flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 bg-indigo-50 rounded-full text-indigo-600">
                {/* Modern Mic/Globe Icon */}
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-900">
                {t("chatInput.selectLanguage")}
              </h2>
            </div>

            {/* Selectable List (Replaces the native select dropdown) */}
            <div className="p-6 space-y-2.5 max-h-[60vh] overflow-y-auto">
              {[
                { id: "en-US", name: "English" },
                { id: "hi-IN", name: "Hindi" },
                { id: "te-IN", name: "Telugu" },
                { id: "ta-IN", name: "Tamil" },
                { id: "mr-IN", name: "Marathi" },
              ].map((lang) => (
                <button
                  key={lang.id}
                  onClick={() => setSelectedLanguage(lang.id)}
                  className={`w-full flex items-center justify-between px-4 py-3.5 rounded-2xl border transition-all duration-200 ${
                    selectedLanguage === lang.id
                      ? "border-indigo-600 bg-indigo-50/50 text-indigo-700"
                      : "border-gray-100 bg-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-200"
                  }`}
                >
                  <span className="font-semibold text-[15px]">{lang.name}</span>

                  {/* Selected Checkmark */}
                  {selectedLanguage === lang.id && (
                    <svg
                      className="w-5 h-5 text-indigo-600 animate-in zoom-in"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </button>
              ))}
            </div>

            {/* Footer Actions */}
            <div className="p-6 pt-2 flex items-center justify-between gap-3">
              <button
                className="flex-1 px-4 py-3.5 text-sm font-semibold text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-2xl transition-all"
                onClick={() => setShowLanguageModal(false)}
              >
                {t("chatInput.cancel")}
              </button>
              <button
                className="flex-1 px-4 py-3.5 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-2xl shadow-lg shadow-indigo-600/20 transition-all active:scale-[0.98]"
                onClick={() => {
                  setShowLanguageModal(false);
                  startRecordingWithLanguage(selectedLanguage);
                }}
              >
                {t("chatInput.start")}
              </button>
            </div>
          </div>
        </div>
      )}
    </footer>
  );
}
